// The DO goes back to being a file (Alan, 2026-08-03). It was a link for one day,
// which meant the delivery order lived wherever the customer happened to have put
// it - a Drive share that expires, a mail attachment nobody else can open - and the
// Task carried a URL that only sometimes resolved to a document.
//
// The backend never lost the ability to take the file: prepareContainerTaskDocumentUpload
// in functions/src/container-task-document.ts still accepts a do_document and writes
// it to Storage under the actor and request. This is the browser half of that
// contract, and the four keys below are exactly what it validates - it rejects any
// other key set outright, so this shape is not adjustable from here.

export const DELIVERY_ORDER_CONTENT_TYPES = Object.freeze({
  'application/pdf': 'pdf',
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/heic': 'heic',
  'image/heif': 'heif',
});

export const DELIVERY_ORDER_MAX_BYTES = 10 * 1024 * 1024;

export const DELIVERY_ORDER_ACCEPT = Object.keys(DELIVERY_ORDER_CONTENT_TYPES).join(',');

export function describeDeliveryOrderFile(file) {
  if (!file || typeof file !== 'object') return null;
  const name = typeof file.name === 'string' ? file.name.trim() : '';
  const type = typeof file.type === 'string' ? file.type.trim().toLowerCase() : '';
  const size = Number(file.size);
  if (!name || name.length > 255) return { error: 'Give the delivery order a shorter file name.' };
  if (!DELIVERY_ORDER_CONTENT_TYPES[type]) {
    return { error: 'The delivery order must be a PDF or an image.' };
  }
  if (!Number.isSafeInteger(size) || size < 1) return { error: 'That file is empty.' };
  if (size > DELIVERY_ORDER_MAX_BYTES) return { error: 'The delivery order must be under 10 MB.' };
  return { name, type, size };
}

function base64FromBytes(bytes) {
  let binary = '';
  for (let index = 0; index < bytes.length; index += 1) binary += String.fromCharCode(bytes[index]);
  return btoa(binary);
}

/**
 * Reads the file and returns exactly the four keys the callable accepts. size_bytes
 * is taken from the bytes actually read, not from file.size - the server compares
 * the two and rejects a mismatch, and reading is the half that can truncate.
 */
export async function readDeliveryOrderDocument(file) {
  const described = describeDeliveryOrderFile(file);
  if (!described) throw new TypeError('A delivery order file is required.');
  if (described.error) throw new Error(described.error);
  const buffer = await file.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  if (bytes.length < 1 || bytes.length > DELIVERY_ORDER_MAX_BYTES) {
    throw new Error('The delivery order could not be read.');
  }
  return {
    content_type: described.type,
    data_base64: base64FromBytes(bytes),
    original_name: described.name,
    size_bytes: bytes.length,
  };
}

/**
 * The Storage object a stored DO names, or null (2026-09-27: "do上传之后那个链接为什么
 * 不在那个柜子tab显示").
 *
 * Only the create callables' own folder is accepted - every one of the 138 DOs in
 * production is container-task-documents/{uploader}/{request}/delivery-order__{24 hex}
 * - because this path goes straight to the Storage SDK, and a hand-typed do_ref
 * pointing anywhere else must not become a download.
 */
export function deliveryOrderObjectPath(value) {
  const text = typeof value === 'string' ? value.trim() : '';
  const match = /^gs:\/\/[a-z0-9._-]+\/(container-task-documents\/[A-Za-z0-9_-]+\/[A-Za-z0-9_-]+\/delivery-order__[a-f0-9]{24}\.(?:pdf|jpg|png|webp|heic|heif))$/.exec(text);
  return match ? match[1] : null;
}

/** An http(s) DO, from the one day it was a link (2026-08-02), or null. */
export function deliveryOrderLink(value) {
  const text = typeof value === 'string' ? value.trim() : '';
  return /^https?:\/\/\S+$/i.test(text) ? text : null;
}

/** What the Task detail should show for a stored DO. */
export function deliveryOrderLabel(value) {
  const text = typeof value === 'string' ? value.trim() : '';
  if (!text) return '';
  // Uploaded documents are stored as gs:// URIs. A raw bucket path is not something
  // to put in front of an operator, so show the file it points at.
  if (text.startsWith('gs://')) {
    const name = text.split('/').pop() || '';
    return name.startsWith('delivery-order__') ? 'Delivery order (uploaded)' : name || 'Delivery order';
  }
  return text;
}
